/**
 * Per-account Djay variant selection hook.
 *
 * Scopes the selected mascotte look (variant + accessory + tier + treatment)
 * to the active learner account. When switching accounts, each account gets
 * their own look (or the defaults).
 *
 * DDD co-locatie: moved from components/mascot/ in s60.
 * Fase 2 dataLayer migration: localStorage calls replaced by dataLayer.
 *
 * REACTIVITY FIX: Uses a module-level store with listeners so ALL consumers
 * of useDjaySelection() stay in sync when any one of them changes a field.
 *
 * Audit 2026: dubbele effect (mount + prev-ref) samengevoegd tot één
 * effect met [accountId] deps (H8). Zie docs/audits/MASCOT_DOMAIN_AUDIT_2026.md §3.1.
 *
 * 2026 Djay Locker: added the `treatment` finish axis (none/glow/gummy/
 * sparkle/aura) as a 4th persisted field, mirroring the variant/accessory/tier
 * generation-token pattern.
 */

import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { DjayVariant, DjayAccessory, DjayTier, DjayTreatment } from './DjayAvatar';
import { useAppStore } from '../app/hooks/useAppStore';
import { dataLayer } from '../data/dataLayer';
import { warn as logWarn } from '../utils/logger';
const DEFAULT_VARIANT: DjayVariant = 'default';
const DEFAULT_ACCESSORY: DjayAccessory = 'none';
const DEFAULT_TIER: DjayTier = 'rookie';
const DEFAULT_TREATMENT: DjayTreatment = 'none';

// ── Validatie van opgeslagen waarden ─────────────────────────────────
//
// Wat uit de opslag komt is een string, geen `DjayVariant`. De laadfunctie
// castte die ongevalideerd door. Een oude of beschadigde waarde belandde zo
// als "geldige" variant in de render, waar hij geen kleurenschema heeft — het
// kind ziet dan een halve of onzichtbare Djay, en er is niets dat aangeeft
// waaróm.
//
// De `Record<X, true>`-vorm is bewust: TypeScript dwingt af dat elke variant
// hier een sleutel heeft. Komt er een nieuwe bij, dan faalt de compilatie in
// plaats van dat de waarde stilletjes ongeldig wordt.
const VALID_VARIANTS: Record<DjayVariant, true> = {
  default: true,
  blaze: true,
  frost: true,
  pixel: true,
  royal: true,
  cosmic: true,
  shadow: true,
  goud: true,
  rainbow: true
};
const VALID_TIERS: Record<DjayTier, true> = {
  rookie: true,
  pro: true,
  master: true
};
const VALID_TREATMENTS: Record<DjayTreatment, true> = {
  none: true,
  glow: true,
  gummy: true,
  sparkle: true,
  aura: true
};
const VALID_ACCESSORIES: Record<DjayAccessory, true> = {
  none: true,
  seedling: true,
  pencil: true,
  badge100: true,
  smallFlame: true,
  trophy: true,
  doubleFlameCrown: true,
  starWand: true,
  tripleStars: true,
  scepter: true,
  leafGarland: true,
  medal: true,
  cosmicOrb: true,
  shadowOrb: true,
  sunglasses: true,
  cap: true,
  goldChain: true
};
function coerce<T extends string>(stored: unknown, valid: Record<T, true>, fallback: T, field: string): T {
  if (typeof stored === 'string' && stored in valid) return stored as T;
  if (stored != null) {
    // Luidruchtig, want een onbekende waarde betekent dat er ooit iets is
    // opgeslagen dat we niet meer kennen — dat wil je zien, niet wegpoetsen.
    logWarn(`[useDjaySelection] Onbekende ${field} in opslag genegeerd`, stored);
  }
  return fallback;
}

// ── Module-level shared store ────────────────────────────────────────
// All useDjaySelection() instances subscribe to this store so changes
// in FloatingDjay are instantly reflected in ProgressPage and vice versa.

type Listener = () => void;
interface SelectionState {
  variant: DjayVariant;
  accessory: DjayAccessory;
  tier: DjayTier;
  treatment: DjayTreatment;
}
let _currentState: SelectionState = {
  variant: DEFAULT_VARIANT,
  accessory: DEFAULT_ACCESSORY,
  tier: DEFAULT_TIER,
  treatment: DEFAULT_TREATMENT
};
let _currentAccountId: string | null = null;
/**
 * Monotonic generation token. Bumped on every account-switch load AND
 * on every explicit write. An in-flight load only applies
 * its result if the generation hasn't moved — this guards against the
 * "user picks variant before initial load resolves" race that would
 * otherwise overwrite the user's choice with the stored value.
 * (Audit RC ronde 2 2026.)
 */
let _generation = 0;
const _listeners = new Set<Listener>();
function getSnapshot(): SelectionState {
  return _currentState;
}
function subscribe(listener: Listener): () => void {
  _listeners.add(listener);
  return () => {
    _listeners.delete(listener);
  };
}
function _notify() {
  _listeners.forEach(l => l());
}

/**
 * Wegschrijven van één cosmetisch veld.
 *
 * De vier setters riepen `void dataLayer.save…()` aan zónder `.catch()`. Bij
 * een schrijffout leverde dat een onafgehandelde promise-rejection op, en —
 * erger — het scherm toonde de nieuwe keuze gewoon: het kind zag zijn nieuwe
 * Djay, en bij de volgende sessie stond de oude er weer. Geen melding, geen
 * spoor, en niets waaruit het kind kan opmaken dat het niet aan hem lag.
 *
 * De keuze wordt bewust nog steeds meteen zichtbaar (optimistisch): wachten op
 * de opslag zou elke stijlwissel merkbaar traag maken voor een fout die vrijwel
 * nooit optreedt. Wat verandert is dat de fout nu zíchtbaar is in de log in
 * plaats van te verdwijnen.
 */
function _persist(promise: Promise<unknown>, field: string) {
  promise.catch(err => {
    logWarn(`[useDjaySelection] Opslaan van ${field} mislukt`, err);
  });
}
function _setVariant(variant: DjayVariant, accountId: string | null) {
  _generation += 1;
  _currentState = {
    ..._currentState,
    variant
  };
  _notify();
  _persist(dataLayer.saveDjayVariant(accountId, variant === DEFAULT_VARIANT ? null : variant), 'variant');
}
function _setAccessory(accessory: DjayAccessory, accountId: string | null) {
  _generation += 1;
  _currentState = {
    ..._currentState,
    accessory
  };
  _notify();
  _persist(dataLayer.saveDjayAccessory(accountId, accessory === DEFAULT_ACCESSORY ? null : accessory), 'accessory');
}
function _setTier(tier: DjayTier, accountId: string | null) {
  _generation += 1;
  _currentState = {
    ..._currentState,
    tier
  };
  _notify();
  _persist(dataLayer.saveDjayTier(accountId, tier === DEFAULT_TIER ? null : tier), 'tier');
}
function _setTreatment(treatment: DjayTreatment, accountId: string | null) {
  _generation += 1;
  _currentState = {
    ..._currentState,
    treatment
  };
  _notify();
  _persist(dataLayer.saveDjayTreatment(accountId, treatment === DEFAULT_TREATMENT ? null : treatment), 'treatment');
}
async function _loadSelectionForAccount(accountId: string | null) {
  _generation += 1;
  const gen = _generation;
  _currentAccountId = accountId;
  const [storedVariant, storedAccessory, storedTier, storedTreatment] = await Promise.all([dataLayer.getDjayVariant(accountId), dataLayer.getDjayAccessory(accountId), dataLayer.getDjayTier(accountId), dataLayer.getDjayTreatment(accountId)]);

  // Apply only if neither (a) the account changed nor (b) a write
  // happened during the await — both bump the generation.
  if (_currentAccountId === accountId && _generation === gen) {
    _currentState = {
      variant: coerce(storedVariant, VALID_VARIANTS, DEFAULT_VARIANT, 'variant'),
      accessory: coerce(storedAccessory, VALID_ACCESSORIES, DEFAULT_ACCESSORY, 'accessory'),
      tier: coerce(storedTier, VALID_TIERS, DEFAULT_TIER, 'tier'),
      treatment: coerce(storedTreatment, VALID_TREATMENTS, DEFAULT_TREATMENT, 'treatment')
    };
    _notify();
  }
}

// ── Public hook ──────────────────────────────────────────────────────

export function useDjaySelection() {
  const {
    activeAccount
  } = useAppStore();
  const accountId = activeAccount?.id ?? null;

  // Single effect: load on mount AND on account-switch.
  useEffect(() => {
    _loadSelectionForAccount(accountId);
  }, [accountId]);

  // Subscribe to the shared store
  const state = useSyncExternalStore(subscribe, getSnapshot);
  const setSelectedVariant = useCallback((variant: DjayVariant) => {
    _setVariant(variant, accountId);
  }, [accountId]);
  const setSelectedAccessory = useCallback((accessory: DjayAccessory) => {
    _setAccessory(accessory, accountId);
  }, [accountId]);
  const setSelectedTier = useCallback((tier: DjayTier) => {
    _setTier(tier, accountId);
  }, [accountId]);
  const setSelectedTreatment = useCallback((treatment: DjayTreatment) => {
    _setTreatment(treatment, accountId);
  }, [accountId]);
  return {
    selectedVariant: state.variant,
    setSelectedVariant,
    selectedAccessory: state.accessory,
    setSelectedAccessory,
    selectedTier: state.tier,
    setSelectedTier,
    selectedTreatment: state.treatment,
    setSelectedTreatment
  };
}